import { createContext, useContext, useMemo, useEffect, Dispatch, SetStateAction, PropsWithChildren } from "react";
import usePersistedState from "../hooks/persisted-state.hook";
import errorMessages from "../translations/en/errors.json";
import { darkTheme, lightTheme } from "../theme";
import { Theme, ThemeProvider } from "@material-ui/core/styles";

const prefersDark = window ? window?.matchMedia("(prefers-color-scheme: dark)").matches || false : false;

export type ThemeType = "default" | "dark";

const defaultThemeType = prefersDark ? "dark" : "default";

export interface ThemeContext {
  themeType: ThemeType;
  setThemeType: Dispatch<SetStateAction<ThemeType>>;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  theme: Theme;
}

const Context = createContext<ThemeContext>(null!);

function ThemeContextProvider(props: PropsWithChildren<{}>): JSX.Element {
  const [themeType, setThemeType] = usePersistedState<ThemeType>(`context/theme`, {
    storageType: "local",
    defaultValue: defaultThemeType,
  });

  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedState<boolean>(`context/theme/sidebar-collapsed`, {
    storageType: "local",
    defaultValue: false,
  });

  // Theme is a side effect of the string theme above. Collapsing the sidebar narrows
  // sidebar.fixedWidth, so everything laid out around the sidebar (content offset, fixed page
  // loaders, sticky page footers, alerts) follows without each needing to know about it.
  const theme: Theme = useMemo(() => {
    const baseTheme = themeType === "dark" ? darkTheme : lightTheme;

    if (!sidebarCollapsed) {
      return baseTheme;
    }

    return {
      ...baseTheme,
      sidebar: {
        ...baseTheme.sidebar,
        fixedWidth: baseTheme.sidebar.collapsedWidth,
        collapsed: true,
      },
    };
  }, [themeType, sidebarCollapsed]);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      themeType === "dark" ? "dark" : "light"
    );
  }, [themeType]);

  const contextValue: ThemeContext = {
    themeType,
    setThemeType,
    sidebarCollapsed: !!sidebarCollapsed,
    setSidebarCollapsed,
    theme,
  };

  return (
    <Context.Provider value={{ ...contextValue }}>
      <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
    </Context.Provider>
  );
}

function useThemeContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(errorMessages.context.useHookWithinProvider);
  }
  return context;
}

export { ThemeContextProvider, useThemeContext };
